import type { AirshowJunctionFeedbackEvidence } from "./airshowJunctionFeedback";
import {
  airshowAlignmentMatrix,
  airshowAlignmentSignature,
  type AirshowAlignmentMode,
  type AirshowCameraEvidence,
  type AirshowGeometryMode,
  type AirshowLoomFidelity,
  type AirshowManualAlignment,
  type AirshowReviewLayout,
  type AirshowReviewResponse,
  type AirshowServerAlignment,
  type AirshowSourceFrame,
} from "./airshowReviewContract";

export interface AirshowReviewFeedback {
  schema: "loom.airshow-review-feedback/v1";
  frame: AirshowSourceFrame;
  server_alignment: AirshowServerAlignment;
  quantitative_comparison_allowed: boolean;
  catalog: { catalog_id: string; catalog_revision: string };
  model: {
    entry_id: string;
    label: string;
    archive_member_path: string;
    source_sha256: string;
  };
  evidence: {
    reference: {
      status: "available" | "unavailable";
      representation: "historical-x3d-preview" | "unavailable";
      trust: "historical-unattested" | "unavailable";
      artifact_sha256: string | null;
      code: string | null;
      message: string | null;
      warnings: readonly string[];
    };
    loom: {
      status: "available" | "unavailable";
      representation: AirshowGeometryMode;
      engine: "loom-openvsp-adapter" | "unavailable";
      fidelity: AirshowLoomFidelity;
      bundle_key: string | null;
      geometry_complete: boolean;
      skipped_surface_count: number;
      export_order_attested: boolean;
      code: string | null;
      message: string | null;
      warnings: readonly string[];
    };
  };
  view: {
    layout: AirshowReviewLayout;
    camera_sync: AirshowCameraEvidence;
    overlay_opacity: number;
    visibility: { reference: boolean; loom: boolean };
    alignment_mode: AirshowAlignmentMode;
    alignment_signature: string;
    matrix_order: "row-major";
    candidate_to_reference: readonly number[];
    manual_alignment: {
      translation: readonly [number, number, number];
      rotation_degrees: readonly [number, number, number];
      scale: number;
    };
  };
  junction?: AirshowJunctionFeedbackEvidence;
  note: string;
}

interface FeedbackInput {
  catalog: { catalogId: string; catalogRevision: string };
  entry: { id: string; label: string; archiveMemberPath: string; sourceSha256: string };
  review: AirshowReviewResponse;
  view: {
    layout: AirshowReviewLayout;
    camera: AirshowCameraEvidence;
    overlayOpacity: number;
    visibility: { reference: boolean; loom: boolean };
    alignment: {
      mode: AirshowAlignmentMode;
      manual: AirshowManualAlignment;
      resolved?: AirshowManualAlignment;
    };
  };
  junction?: AirshowJunctionFeedbackEvidence;
  note: string;
}

function bareSha256(value: string): string {
  return value.replace(/^sha256:/, "");
}

function assertJunctionIdentity(input: FeedbackInput): void {
  const junction = input.junction;
  if (!junction) return;
  if (junction.entry_id !== input.entry.id
      || junction.archive_member_path !== input.entry.archiveMemberPath
      || junction.source_sha256 !== bareSha256(input.entry.sourceSha256)
      || input.review.loom.status !== "available"
      || junction.review_bundle_key !== input.review.loom.bundle_key) {
    throw new Error("Airshow junction evidence does not match the active review identity");
  }
}

export function createAirshowReviewFeedback(input: FeedbackInput): AirshowReviewFeedback {
  assertJunctionIdentity(input);
  const { catalog, entry, review, view } = input;
  const resolvedAlignment = view.alignment.resolved ?? view.alignment.manual;
  const alignmentSignature = airshowAlignmentSignature(view.alignment.mode, resolvedAlignment);
  return {
    schema: "loom.airshow-review-feedback/v1",
    frame: { ...review.source_frame, axes: [...review.source_frame.axes] },
    server_alignment: {
      mode: review.alignment.mode,
      candidate_to_reference: [...review.alignment.candidate_to_reference],
    },
    quantitative_comparison_allowed:
      review.quantitative_comparison_allowed && view.alignment.mode === "source-frame",
    catalog: { catalog_id: catalog.catalogId, catalog_revision: catalog.catalogRevision },
    model: {
      entry_id: entry.id,
      label: entry.label,
      archive_member_path: entry.archiveMemberPath,
      source_sha256: bareSha256(entry.sourceSha256),
    },
    evidence: {
      reference: {
        status: review.reference.status,
        representation: review.reference.representation,
        trust: review.reference.status === "available" ? review.reference.trust : "unavailable",
        artifact_sha256: review.reference.artifact_sha256,
        code: review.reference.status === "unavailable" ? review.reference.code : null,
        message: review.reference.status === "unavailable" ? review.reference.message : null,
        warnings: [...review.reference.warnings],
      },
      loom: {
        status: review.loom.status,
        representation: review.loom.representation,
        engine: review.loom.status === "available" ? review.loom.engine : "unavailable",
        fidelity: review.loom.fidelity,
        bundle_key: review.loom.bundle_key,
        geometry_complete: review.loom.geometry_complete,
        skipped_surface_count: review.loom.skipped_surfaces.length,
        export_order_attested: review.loom.export_order_attestation !== null,
        code: review.loom.status === "unavailable" ? review.loom.code : null,
        message: review.loom.status === "unavailable" ? review.loom.message : null,
        warnings: [...review.loom.warnings],
      },
    },
    view: {
      layout: view.layout,
      camera_sync: { ...view.camera },
      overlay_opacity: view.overlayOpacity,
      visibility: { ...view.visibility },
      alignment_mode: view.alignment.mode,
      alignment_signature: alignmentSignature,
      matrix_order: "row-major",
      candidate_to_reference: view.alignment.mode === "source-frame"
        ? [...review.alignment.candidate_to_reference]
        : airshowAlignmentMatrix(resolvedAlignment),
      manual_alignment: {
        translation: [...view.alignment.manual.translation],
        rotation_degrees: [...view.alignment.manual.rotationDegrees],
        scale: view.alignment.manual.scale,
      },
    },
    ...(input.junction ? { junction: input.junction } : {}),
    note: input.note,
  };
}

export function serializeAirshowReviewFeedback(feedback: AirshowReviewFeedback): string {
  return JSON.stringify(feedback, null, 2);
}

export function airshowFeedbackFilename(entryId: string): string {
  const segments = entryId.split("/");
  const basename = segments[segments.length - 1]?.replace(/[^a-z0-9._-]+/gi, "-") || "model";
  return `openvsp-review-${basename.replace(/\.[^.]+$/, "")}.json`;
}
